import { useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from './Icon';
import { Photo, rise } from './UI';
import { useApp } from '../context/AppContext';
import { api } from '../utils/api';
import { cmToFeet } from '../utils/format';

export function useProfileActions() {
  const { toast, refreshCounts } = useApp();
  const sendInterest = async (p, onDone) => {
    try {
      const r = await api(`/interests/${p.profileId}`, { method: 'POST' });
      toast(r.message, 'success');
      onDone?.(r);
      refreshCounts();
    } catch (e) {
      toast(e.message, 'error');
    }
  };
  const toggleShortlist = async (p, onDone) => {
    try {
      const r = await api(`/profiles/${p.profileId}/shortlist`, { method: 'POST' });
      toast(r.message);
      onDone?.(r.shortlisted);
    } catch (e) {
      toast(e.message, 'error');
    }
  };
  return { sendInterest, toggleShortlist };
}

export default function ProfileCard({ p, compact = false, footer }) {
  const navigate = useNavigate();
  const { sendInterest, toggleShortlist } = useProfileActions();
  const [sent, setSent] = useState(false);
  const [starred, setStarred] = useState(p.shortlisted);

  // Gentle 3D tilt following the pointer.
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 200, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 200, damping: 20 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  const open = () => navigate(`/profile/${p.profileId}`);
  const stop = (fn) => (e) => {
    e.stopPropagation();
    fn();
  };

  return (
    <motion.article
      variants={rise}
      className={`pcard ${compact ? 'pcard--compact' : ''}`}
      style={{ rotateX: rx, rotateY: ry }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      whileHover={{ y: -6 }}
      onClick={open}
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && open()}
    >
      <div className="pcard__media">
        <Photo src={p.photo} alt={p.name} locked={p.photoLocked} />
        <div className="pcard__shade" />
        <div className="pcard__top">
          {p.matchScore ? <span className="pill pill--glass"><Icon name="sparkle" size={13} /> {p.matchScore}% match</span> : <span />}
          <motion.button
            whileTap={{ scale: 0.8 }}
            className={`star-btn ${starred ? 'is-on' : ''}`}
            onClick={stop(() => toggleShortlist(p, setStarred))}
            aria-label="Shortlist"
          >
            <Icon name="star" size={18} fill={starred ? 'currentColor' : 'none'} />
          </motion.button>
        </div>
        <div className="pcard__info">
          <h3>
            {p.name}
            {p.verified && <Icon name="shield" size={16} className="verified" />}
          </h3>
          <p>
            {[p.age && `${p.age} yrs`, cmToFeet(p.heightCm), p.city].filter(Boolean).join(' · ')}
          </p>
          {!compact && <p className="pcard__job">{[p.specialty || p.profession, p.employer].filter(Boolean).join(' at ')}</p>}
        </div>
        {p.online && <span className="online-badge">Online</span>}
      </div>
      {!compact && (
        <div className="pcard__body">
          <div className="pcard__tags">
            {[p.religion, p.motherTongue, p.income].filter(Boolean).map((t) => (
              <span key={t} className="tag">{t}</span>
            ))}
          </div>
          {footer || (
            <motion.button
              className={`btn btn--block ${sent ? 'btn--soft' : 'btn--primary'}`}
              whileTap={{ scale: 0.96 }}
              disabled={sent}
              onClick={stop(() => sendInterest(p, () => setSent(true)))}
            >
              <Icon name={sent ? 'check' : 'heart'} size={17} fill={sent ? 'none' : 'currentColor'} stroke={sent ? 2.2 : 0} />
              {sent ? 'Interest sent' : 'Send interest'}
            </motion.button>
          )}
        </div>
      )}
    </motion.article>
  );
}

export function CardGrid({ items, render }) {
  return (
    <motion.div className="grid-cards" variants={{ hidden: {}, show: { transition: { staggerChildren: 0.05 } } }} initial="hidden" animate="show">
      {items.map((p) => (render ? render(p) : <ProfileCard key={p.profileId} p={p} />))}
    </motion.div>
  );
}
